import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { CreatableSelect } from '@paulownia/shared/components/CreatableSelect';
import { InfoTooltip, type ProcessedImages } from '@paulownia/shared';
import { WizardImagePicker } from './WizardImagePicker';

interface WizardStepDetailsProps {
  name: string;
  shortenName?: string;
  description?: string;
  picture?: string;
  pendingImage: ProcessedImages | null;
  section?: string;
  subSection?: string;
  sections: string[];
  subSectionsBySection: Record<string, string[]>;
  onNameChange: (name: string) => void;
  onShortenNameChange: (shortenName: string | undefined) => void;
  onDescriptionChange: (description: string | undefined) => void;
  onPictureChange: (picture: string | undefined) => void;
  onPendingImageChange: (images: ProcessedImages | null) => void;
  onSectionChange: (section: string) => void;
  onSubSectionChange: (subSection: string | undefined) => void;
}

// Helper to capitalize first letter
function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function WizardStepDetails({
  name,
  shortenName,
  description,
  picture,
  pendingImage,
  section,
  subSection,
  sections,
  subSectionsBySection,
  onNameChange,
  onShortenNameChange,
  onDescriptionChange,
  onPictureChange,
  onPendingImageChange,
  onSectionChange,
  onSubSectionChange,
}: WizardStepDetailsProps) {
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Détails du produit</h2>
        <p className="text-sm text-gray-500 mt-1">
          Décrivez votre produit pour vos clients.
        </p>
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="name">Nom du produit *</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="ex: Poulet rôti fermier"
        />
      </div>

      {/* Short Name */}
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <Label htmlFor="shortenName">Nom court</Label>
          <InfoTooltip content="Utilisé lors de la création de commande pour un affichage plus compact" />
        </div>
        <Input
          id="shortenName"
          value={shortenName || ''}
          onChange={(e) => onShortenNameChange(e.target.value || undefined)}
          placeholder="ex: Poulet"
        />
      </div>

      {/* Section */}
      <div className="space-y-2">
        <Label>Section *</Label>
        <CreatableSelect
          value={section || ''}
          onChange={(v) => {
            onSectionChange(v);
            // Reset subSection when section changes
            if (v !== section) {
              onSubSectionChange(undefined);
            }
          }}
          options={sections}
          placeholder="Sélectionner ou créer..."
          formatDisplay={capitalize}
        />
      </div>

      {/* SubSection */}
      {section && (
        <div className="space-y-2">
          <Label>Sous-section</Label>
          <CreatableSelect
            value={subSection || ''}
            onChange={(v) => onSubSectionChange(v || undefined)}
            options={subSectionsBySection[section.toLowerCase()] || []}
            placeholder="Sélectionner ou créer..."
            formatDisplay={capitalize}
          />
        </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description (visible clients)</Label>
        <Input
          id="description"
          value={description || ''}
          onChange={(e) => onDescriptionChange(e.target.value || undefined)}
          placeholder="Description courte du produit"
        />
      </div>

      {/* Image */}
      <WizardImagePicker
        pendingImage={pendingImage}
        pictureUrl={picture}
        onImageProcessed={(images) => onPendingImageChange(images)}
        onImageRemoved={() => onPendingImageChange(null)}
        onPictureUrlChange={onPictureChange}
      />
    </div>
  );
}
